import './Home.css';
import { useState, useEffect, useRef, useCallback } from "react";
import { FaCar, FaMoneyBillWave, FaCheckCircle, FaArrowRight, FaChevronDown, FaChevronUp, FaInfoCircle, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import { BsInfoCircleFill } from 'react-icons/bs';
import { Tooltip } from 'react-tooltip';
import { Link } from 'react-router-dom';

async function fetchJson(url, options) {
    const resp = await fetch(url, options);
    if (!resp.ok) {
        throw new Error(`Request failed: ${resp.status}`);
    }
    return resp.json();
}

const attributes = [
    "marke",
    "modell",
    "baujahr",
    "bauform",
    "kraftstoff",
    "kw_ps", // Combine kw and ps
    "modellvariante",
    "getriebeart",
    "tueren",
    "modelltyp",
    "preis"
];

export default function Home() {
    const [options, setOptions] = useState({});
    const [markenOptions, setMarkenOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState({});
    const [kilometerstand, setKilometerstand] = useState("");
    const [currentStep, setCurrentStep] = useState(1);
    const [calculating, setCalculating] = useState(false);
    const [price, setPrice] = useState(null);
    const [expectedPrice, setExpectedPrice] = useState("");
    const [expectedPriceTouched, setExpectedPriceTouched] = useState(false);
    // const [showChat, setShowChat] = useState(false);
    const [faqOpen, setFaqOpen] = useState({});
    // const [chatMessage, setChatMessage] = useState("");
    // const [chatMessages, setChatMessages] = useState([
    //     { sender: 'system', text: 'Willkommen bei autozack.de! Wie können wir Ihnen helfen?' }
    // ]);
    // const chatEndRef = useRef(null);
    // Add ref for the latest dropdown
    const latestDropdownRef = useRef(null);
    // Add ref for the price display container
    const priceDisplayRef = useRef(null);
    // Add ref for the form card element
    const formCardRef = useRef(null);
    // Track step transitions so we only auto-calculate once when entering step 3
    const prevStepRef = useRef(currentStep);
    // Track and cancel pending calculation timers to avoid stacked timeouts
    const priceTimerRef = useRef(null);

    const fetchMarken = useCallback(async () => {
        try {
            const data = await fetchJson("https://api.autozack.de/marken");
            setMarkenOptions(data);
            setOptions({
                marke: data
            });
        } catch (error) {
            console.error("Error fetching marken:", error);
        }
    }, []);

    const handleChange = useCallback((attribute, value) => {
        const attrIndex = attributes.indexOf(attribute);
        
        setSelectedValues((prevValues) => {
            const newValues = { ...prevValues, [attribute]: value };

            // Clear all subsequent attributes
            for (let i = attrIndex + 1; i < attributes.length; i++) {
                delete newValues[attributes[i]];
            }

            return newValues;
        });

        // Clear options for subsequent attributes
        if (attrIndex >= 0) {
            setOptions((prevOptions) => {
                const nextOptions = { ...prevOptions };
                for (let i = attrIndex + 1; i < attributes.length; i++) {
                    delete nextOptions[attributes[i]];
                }
                return nextOptions;
            });

            // Reset step and price if needed
            if (attrIndex < 6 && currentStep > 1) {
                setCurrentStep(1);
                setPrice(null);
                setCalculating(false);
            } else if (attrIndex < 10 && currentStep > 2) {
                setCurrentStep(2);
                setPrice(null);
                setCalculating(false);
            }
        }
    }, [currentStep]);

    const fetchFilteredOptions = useCallback(async () => {
        // Only use attributes that have valid (non-null, non-undefined, non-empty) values
        const queryString = Object.keys(selectedValues)
        .filter((key) => {
            const value = selectedValues[key];
            // Skip modelltyp if it's "Standard" (represents empty in database)
            if (key === 'modelltyp' && value === 'Standard') {
                return false;
            }
            return value !== null && value !== undefined && value !== "";
        })
        .map((key) => {
            if (key === 'kw_ps') {
                const [kw, ps] = selectedValues[key].split('/');
                return `${encodeURIComponent('kw')}=${encodeURIComponent(kw)}&${encodeURIComponent('ps')}=${encodeURIComponent(ps)}`;
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(selectedValues[key])}`;
        })
        .join("&");

        // Only fetch if we have at least one valid parameter
        if (!queryString) {
            return;
        }

        try {
            const data = await fetchJson(`https://api.autozack.de/search?${queryString}`);

            const newOptions = attributes.reduce((acc, attr) => {
                if (attr === "kw_ps") {
                    acc[attr] = [...new Set(data.map((car) => `${car.kw}/${car.ps}`))];
                } else if (attr === "marke") {
                    acc[attr] = markenOptions.length ? markenOptions : (options.marke || []);
                } else if (attr === "modelltyp") {
                    // Replace empty strings with "Standard" for better UX
                    acc[attr] = [...new Set(data.map((car) => {
                        const value = car[attr];
                        return (value === null || value === undefined || value === "") ? "Standard" : value;
                    }))];
                } else {
                    acc[attr] = [...new Set(data.map((car) => car[attr]))];
                }
                return acc;
            }, {});

            // Replace options entirely instead of merging to ensure clean slate
            setOptions((prevOptions) => {
                const cleanedOptions = {};
                // Keep marke options
                cleanedOptions.marke = prevOptions.marke || [];
                
                // Add new options for attributes that have data
                Object.keys(newOptions).forEach(key => {
                    if (newOptions[key] && newOptions[key].length > 0) {
                        // If this attribute already has a selected value, keep the previous options
                        // so users can easily change their selection
                        if (selectedValues[key]) {
                            cleanedOptions[key] = prevOptions[key] || newOptions[key];
                        } else {
                            cleanedOptions[key] = newOptions[key];
                        }
                    }
                });
                
                return cleanedOptions;
            });
        } catch (error) {
            console.error("Error fetching cars:", error);
        }
    }, [markenOptions, options.marke, selectedValues]);

    // const calculatePrice = useCallback(() => {
    //     if (priceTimerRef.current) {
    //         clearTimeout(priceTimerRef.current);
    //         priceTimerRef.current = null;
    //     }
    //     setCalculating(true);
    //     priceTimerRef.current = setTimeout(() => {
    //         let basePrice = 0;
    //         
    //         // Get price from selectedValues if available
    //         if (selectedValues.preis) {
    //             basePrice = parseFloat(selectedValues.preis);
    //         } 
    //         // Use first price from options if available
    //         else if (options.preis && options.preis.length > 0) {
    //             basePrice = parseFloat(options.preis[0]);
    //         } 
    //         // Fallback to a calculated estimate
    //         else {
    //             // Use basic calculation based on available attributes
    //             basePrice = 10000; // Default starting value
    //             
    //             // Adjust based on car age
    //             if (selectedValues.baujahr) {
    //                 const carAge = new Date().getFullYear() - parseInt(selectedValues.baujahr);
    //                 basePrice -= carAge * 500; // Reduce value by age
    //             }
    //             
    //             // Adjust for premium brands
    //             if (selectedValues.marke) {
    //                 const premiumBrands = ["BMW", "Mercedes", "Audi", "Porsche"];
    //                 if (premiumBrands.includes(selectedValues.marke)) {
    //                     basePrice *= 1.2; // 20% premium
    //                 }
    //             }
    //         }
    //         
    //         // Adjust for mileage if available
    //         if (kilometerstand) {
    //             const kmFactor = Math.max(0.7, 1 - (parseInt(kilometerstand) / 200000));
    //             basePrice *= kmFactor;
    //         }
    //         
    //         // Ensure price is positive and round to whole euros
    //         basePrice = Math.max(500, Math.round(basePrice));
    //         
    //         setPrice(basePrice);
    //         setCalculating(false);
    //         priceTimerRef.current = null;
    //     }, 1500);
    // }, [kilometerstand, options.preis, selectedValues.baujahr, selectedValues.marke, selectedValues.preis]);

    useEffect(() => {
        return () => {
            if (priceTimerRef.current) {
                clearTimeout(priceTimerRef.current);
                priceTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        fetchMarken();
    }, [fetchMarken]);

    useEffect(() => {
        if (Object.keys(selectedValues).length > 0) {
            fetchFilteredOptions();
        }
    }, [fetchFilteredOptions, selectedValues]);

    useEffect(() => {
        // Auto-fill for step 1 (attributes 0-5) and step 2 (attributes 6-9)
        const startIndex = currentStep === 1 ? 0 : currentStep === 2 ? 6 : -1;
        const endIndex = currentStep === 1 ? 6 : currentStep === 2 ? 10 : -1;
        
        if (startIndex === -1) return;

        const attributesToCheck = attributes.slice(startIndex, endIndex);
        
        for (let i = 0; i < attributesToCheck.length; i++) {
            const attribute = attributesToCheck[i];
            const attributeIndex = startIndex + i;
            
            // Skip auto-fill for modelltyp
            if (attribute === 'modelltyp') {
                continue;
            }
            
            // Check if all previous attributes are filled
            const previousAttributesFilled = attributes.slice(startIndex, attributeIndex).every((prevAttr) => {
                const value = selectedValues[prevAttr];
                return value !== null && value !== undefined && value !== "";
            });
            
            // Check if current attribute is empty
            const currentValue = selectedValues[attribute];
            const currentIsEmpty = currentValue === null || currentValue === undefined || currentValue === "";
            
            // Only auto-fill if previous attributes are filled and current one is empty with single option
            if (previousAttributesFilled && 
                currentIsEmpty && 
                options[attribute] && 
                options[attribute].length === 1) {
                handleChange(attribute, options[attribute][0]);
                break; // Only fill one at a time, let the next render cycle handle the next one
            }
        }
    }, [currentStep, handleChange, options, selectedValues]);

    // useEffect(() => {
    //     if (chatEndRef.current) {
    //         chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    //     }
    // }, [chatMessages]);

    // Auto-calculate only when entering step 3 (prevents recalculation loops)
    useEffect(() => {
        const prev = prevStepRef.current;
        prevStepRef.current = currentStep;

        if (prev !== 3 && currentStep === 3) {
            // Show calculating animation without actually calculating price
            if (priceTimerRef.current) {
                clearTimeout(priceTimerRef.current);
                priceTimerRef.current = null;
            }
            setCalculating(true);
            priceTimerRef.current = setTimeout(() => {
                setCalculating(false);
                priceTimerRef.current = null;
            }, 1500);
        }
    }, [currentStep]);

    // Add this effect to scroll to the newest dropdown when selectedValues changes
    useEffect(() => {
        if (latestDropdownRef.current) {
            setTimeout(() => {
                latestDropdownRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }, 100); // Small delay to ensure the DOM has updated
        }
    }, [selectedValues]);

    // Add this useEffect to scroll to the form card when currentStep changes to 3
    useEffect(() => {
        if (currentStep === 3 && formCardRef.current) {
            setTimeout(() => {
                formCardRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }, 100);
        }
    }, [currentStep]);

    const instructions = {
        marke: "Von welcher Marke ist das Auto?",
        modell: "Welches Modell hat das Auto?",
        baujahr: "In welchem Jahr wurde das Auto zugelassen?",
        bauform: "Welche Bauform hat das Auto?",
        kraftstoff: "Welchen Kraftstoff verwendet das Auto?",
        kw_ps: "Wie viel PS / kW hat das Auto?",
        modellvariante: "Welche Modellvariante hat das Auto?",
        getriebeart: "Welche Getriebeart hat das Auto?",
        tueren: "Wieviele Türen hat das Auto?",
        modelltyp: "Welcher Typ ist das Modell?",
        kilometerstand: "Wie viele Kilometer hat das Auto?",
        preis: "Preis"
    };

    const tooltips = {
        marke: "Wählen Sie die Marke Ihres Fahrzeugs aus der Liste. Wir kaufen alle gängigen Marken.",
        modell: "Wählen Sie das Modell Ihres Fahrzeugs. Die Liste wird basierend auf der ausgewählten Marke gefiltert.",
        baujahr: "Das Erstzulassungsjahr beeinflusst den Wert Ihres Fahrzeugs.",
        bauform: "Die Bauform beschreibt die Karosserieform des Fahrzeugs (z.B. Limousine, Kombi, SUV).",
        kraftstoff: "Wählen Sie den Kraftstofftyp Ihres Fahrzeugs (Benzin, Diesel, Elektro, etc.).",
        kw_ps: "Die Leistung des Motors in Kilowatt (kW) und Pferdestärken (PS).",
        modellvariante: "Spezifische Variante des Modells (z.B. 'Sport', 'Comfort', etc.).",
        getriebeart: "Gibt an, ob Ihr Fahrzeug ein Automatik- oder Schaltgetriebe hat.",
        tueren: "Die Anzahl der Türen des Fahrzeugs, inkl. Heckklappe.",
        modelltyp: "Spezifikation des Modelltyps für eine genauere Bewertung.",
        kilometerstand: "Der Kilometerstand beeinflusst den Wert des Fahrzeugs erheblich. Bitte geben Sie den aktuellen Stand ein."
    };

    const faqData = [
        { 
            question: "Wie funktioniert der Verkaufsprozess?",
            answer: "Unser Verkaufsprozess ist einfach und transparent. Füllen Sie das Formular mit den Details Ihres Fahrzeugs aus, erhalten Sie ein sofortiges Angebot, vereinbaren Sie einen Termin zur Fahrzeuguntersuchung und erhalten Sie Ihr Geld sofort nach Abschluss."
        },
        {
            question: "Wie wird der Preis für mein Auto berechnet?",
            answer: "Der Preis basiert auf verschiedenen Faktoren wie Marke, Modell, Baujahr, Kilometerstand, Zustand und aktuellen Marktbedingungen. Unsere Algorithmen berücksichtigen all diese Faktoren, um Ihnen den fairsten Preis zu bieten."
        },
        {
            question: "Welche Unterlagen benötige ich für den Verkauf?",
            answer: "Sie benötigen: Fahrzeugschein und Fahrzeugbrief, gültige Identitätsnachweise, aktuelle Hauptuntersuchung (TÜV), Serviceheft oder Nachweise über Wartungen und alle vorhandenen Fahrzeugschlüssel."
        },
        {
            question: "Kaufen Sie auch Fahrzeuge mit Mängeln an?",
            answer: "Ja, wir kaufen auch Fahrzeuge mit Mängeln oder Unfallschäden an. Bitte geben Sie bekannte Mängel bei der Bewertung an, damit wir Ihnen ein faires Angebot machen können."
        },
        {
            question: "Wie schnell erhalte ich mein Geld?",
            answer: "Nach erfolgreicher Überprüfung Ihres Fahrzeugs und Abschluss des Kaufvertrags erfolgt die Zahlung sofort – entweder per Überweisung oder als Barzahlung, je nach Ihrer Präferenz."
        }
    ];

    // calculatePrice is defined above via useCallback

    // const handleChatSubmit = (e) => {
    //     e.preventDefault();
    //     if (!chatMessage.trim()) return;

    //     setChatMessages(prev => [...prev, { sender: 'user', text: chatMessage }]);
        
    //     setTimeout(() => {
    //         setChatMessages(prev => [
    //             ...prev, 
    //             { 
    //                 sender: 'agent', 
    //                 text: "Danke für Ihre Nachricht! Ein Mitarbeiter wird sich in Kürze mit Ihnen in Verbindung setzen. Alternativ können Sie uns auch telefonisch kontaktieren." 
    //             }
    //         ]);
    //     }, 1000);
        
    //     setChatMessage("");
    // };

    // Add this new function to check if all required fields for the current step are filled
    const areCurrentStepFieldsFilled = () => {
        if (currentStep === 1) {
            // Check if all fields in step 1 are filled
            return attributes.slice(0, 6).every(attribute => {
                const value = selectedValues[attribute];
                return value !== undefined && value !== null && value !== "";
            });
        } else if (currentStep === 2) {
            // Check if all fields in step 2 are filled
            const attributesComplete = attributes.slice(6, 10).every(attribute => {
                const value = selectedValues[attribute];
                return value !== undefined && value !== null && value !== "";
            });
            
            // Also check kilometerstand if modelltyp is selected
            if (selectedValues["modelltyp"] && (!kilometerstand || kilometerstand === "")) {
                return false;
            }
            
            return attributesComplete;
        }
        
        return false;
    };

    const renderProgressBar = () => {
        const totalSteps = 3;
        return (
            <div className="progress-container">
                <div className="progress-steps">
                    {[...Array(totalSteps)].map((_, index) => (
                        <div 
                            key={index} 
                            className={`progress-step ${index + 1 <= currentStep ? 'active' : ''}`}
                        >
                            <div className="step-number">{index + 1}</div>
                            <div className="step-label">
                                {index === 0 ? 'Fahrzeugdaten' : index === 1 ? 'Zustand' : 'Angebot'}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="progress-bar">
                    <div 
                        className="progress-fill" 
                        style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                    ></div>
                </div>
            </div>
        );
    };

    const renderDropdowns = () => {
        if (currentStep === 1) {
            let lastVisibleDropdownIndex = -1;
            
            // Find the last visible dropdown index
            for (let i = 0; i < attributes.slice(0, 6).length; i++) {
                const prevAttrs = attributes.slice(0, i);
                const isVisible = prevAttrs.every((prevAttr) => 
                    selectedValues[prevAttr] !== null && selectedValues[prevAttr] !== "");
                if (isVisible) {
                    lastVisibleDropdownIndex = i;
                } else {
                    break;
                }
            }
            
            return attributes.slice(0, 6).map((attribute, index) => {
                const isVisible = attributes.slice(0, index).every((prevAttr) => {
                    const value = selectedValues[prevAttr];
                    return value !== null && value !== undefined && value !== "";
                });
                
                // Is this the last visible dropdown?
                const isLastVisible = index === lastVisibleDropdownIndex;
                
                if (attribute === "kw_ps") {
                    return (
                        isVisible && options["kw_ps"] && (
                            <div 
                                key="kw_ps" 
                                className="dropdown-container"
                                ref={isLastVisible ? latestDropdownRef : null}
                            >
                                <div className="label-with-tooltip">
                                    <label>{instructions[attribute]}</label>
                                    <BsInfoCircleFill 
                                        className="tooltip-icon"
                                        data-tooltip-id={`tooltip-${attribute}`}
                                        data-tooltip-content={tooltips[attribute]}
                                    />
                                    <Tooltip id={`tooltip-${attribute}`} className="custom-tooltip" />
                                </div>
                                <select
                                    className="select-dropdown"
                                    value={selectedValues["kw_ps"] || ""}
                                    onChange={(e) => handleChange("kw_ps", e.target.value)}
                                >
                                    <option value="">Bitte wählen</option>
                                    {options["kw_ps"].map((option, optionIndex) => (
                                        <option key={optionIndex} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )
                    );
                }
                
                return (
                    isVisible && options[attribute] && (
                        <div 
                            key={attribute} 
                            className="dropdown-container"
                            ref={isLastVisible ? latestDropdownRef : null}
                        >
                            <div className="label-with-tooltip">
                                <label>{instructions[attribute]}</label>
                                <BsInfoCircleFill 
                                    className="tooltip-icon"
                                    data-tooltip-id={`tooltip-${attribute}`}
                                    data-tooltip-content={tooltips[attribute]}
                                />
                                <Tooltip id={`tooltip-${attribute}`} className="custom-tooltip" />
                            </div>
                            <select
                                className="select-dropdown"
                                value={selectedValues[attribute] || ""}
                                onChange={(e) => handleChange(attribute, e.target.value)}
                            >
                                <option value="">Bitte wählen</option>
                                {options[attribute].map((option, optionIndex) => (
                                    <option key={optionIndex} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    )
                );
            });
        } else if (currentStep === 2) {
            let lastVisibleDropdownIndex = -1;
            
            // Find the last visible dropdown index for step 2
            for (let i = 0; i < attributes.slice(6, 10).length; i++) {
                const attr = attributes[i + 6];
                // Check if all previous attributes in step 2 are filled
                const previousFilled = attributes.slice(6, i + 6).every((prevAttr) => {
                    const value = selectedValues[prevAttr];
                    return value !== null && value !== undefined && value !== "";
                });
                
                if (options[attr] && previousFilled) {
                    lastVisibleDropdownIndex = i;
                } else {
                    break;
                }
            }
            
            // Add +1 for kilometerstand if it's visible
            if (selectedValues["modelltyp"]) {
                lastVisibleDropdownIndex += 1;
            }
            
            return (
                <>
                    {attributes.slice(6, 10).map((attribute, index) => {
                        if (!options[attribute]) return null;
                        
                        // Check if all previous attributes in step 2 are filled
                        const isVisible = attributes.slice(6, index + 6).every((prevAttr) => {
                            const value = selectedValues[prevAttr];
                            return value !== null && value !== undefined && value !== "";
                        });
                        
                        if (!isVisible) return null;
                        
                        // Is this the last visible dropdown?
                        const isLastVisible = index === lastVisibleDropdownIndex && !selectedValues["modelltyp"];
                        
                        return (
                            <div 
                                key={attribute} 
                                className="dropdown-container"
                                ref={isLastVisible ? latestDropdownRef : null}
                            >
                                <div className="label-with-tooltip">
                                    <label>{instructions[attribute]}</label>
                                    <BsInfoCircleFill 
                                        className="tooltip-icon"
                                        data-tooltip-id={`tooltip-${attribute}`}
                                        data-tooltip-content={tooltips[attribute]}
                                    />
                                    <Tooltip id={`tooltip-${attribute}`} className="custom-tooltip" />
                                </div>
                                <select
                                    className="select-dropdown"
                                    value={selectedValues[attribute] || ""}
                                    onChange={(e) => handleChange(attribute, e.target.value)}
                                >
                                    <option value="">Bitte wählen</option>
                                    {options[attribute].map((option, optionIndex) => (
                                        <option key={optionIndex} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                        );
                    })}
                    
                    {selectedValues["modelltyp"] && (
                        <div 
                            className="dropdown-container"
                            ref={latestDropdownRef}  // Always set ref for kilometerstand if visible
                        >
                            <div className="label-with-tooltip">
                                <label>{instructions["kilometerstand"]}</label>
                                <BsInfoCircleFill 
                                    className="tooltip-icon"
                                    data-tooltip-id={`tooltip-kilometerstand`}
                                    data-tooltip-content={tooltips["kilometerstand"]}
                                />
                                <Tooltip id={`tooltip-kilometerstand`} className="custom-tooltip" />
                            </div>
                            <div className="km-input">
                                <input
                                    className="select-dropdown kilometer-input"
                                    type="number"
                                    placeholder="Kilometerstand eingeben"
                                    value={kilometerstand}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value >= 0) {
                                            setKilometerstand(value);
                                        }
                                    }}
                                    min="0"
                                />
                                <span className="suffix">km</span>
                            </div>
                        </div>
                    )}
                </>
            );
        } else if (currentStep === 3) {
            const isExpectedPriceValid = String(expectedPrice).trim() !== '';
            return (
                <div className="price-result-container" ref={priceDisplayRef}>
                    {calculating ? (
                        <div className="calculating">
                            <div className="spinner"></div>
                            <p>Berechne Ihren persönlichen Preis...</p>
                        </div>
                    ) : price ? (
                        <div className="price-display">
                            <div className="price-badge">
                                <span>Unser Angebot</span>
                            </div>
                            <h2>Ihr persönlicher Ankaufspreis</h2>
                            <div className="price-value">€{price.toLocaleString()}</div>
                            <p className="price-note">Dieser Preis ist eine Schätzung basierend auf den von Ihnen angegebenen Daten</p>
                            <div className="price-details">
                                <p>
                                    <strong>Fahrzeug:</strong> {selectedValues.marke} {selectedValues.modell}, {selectedValues.baujahr}
                                </p>
                                <p>
                                    <strong>Motor:</strong> {selectedValues.kraftstoff}, {selectedValues.kw_ps}
                                </p>
                                <p>
                                    <strong>Kilometerstand:</strong> {kilometerstand} km
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="no-price-info">
                            <div className="no-price-header">
                                <FaInfoCircle className="info-icon" />
                                <h3>Individuelles Preisangebot</h3>
                            </div>
                            
                            <div className="vehicle-summary">
                                <p>
                                    <strong>Fahrzeug:</strong> {selectedValues.marke} {selectedValues.modell}, {selectedValues.baujahr}
                                </p>
                                {selectedValues.kraftstoff && selectedValues.kw_ps && (
                                    <p>
                                        <strong>Motor:</strong> {selectedValues.kraftstoff}, {selectedValues.kw_ps}
                                    </p>
                                )}
                                {kilometerstand && (
                                    <p>
                                        <strong>Kilometerstand:</strong> {kilometerstand} km
                                    </p>
                                )}
                            </div>
                            
                            <p className="no-price-message">
                                Der Preis für dieses Fahrzeug ist momentan nicht verfügbar. Sie können ihn direkt über WhatsApp anfragen.
                            </p>
                            
                            <div className="expected-price-container">
                                <div className="label-with-tooltip">
                                    <label>Wunschpreis</label>
                                    <BsInfoCircleFill 
                                        className="tooltip-icon"
                                        data-tooltip-id="tooltip-expected-price"
                                        data-tooltip-content="Geben Sie Ihren Wunschpreis an, um ein passenderes Angebot zu erhalten."
                                    />
                                    <Tooltip id="tooltip-expected-price" className="custom-tooltip" />
                                </div>
                                <div className="price-input">
                                    <input
                                        className="select-dropdown price-input-field"
                                        type="number"
                                        placeholder="Preis in Euro"
                                        value={expectedPrice}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value >= 0 || value === "") {
                                                setExpectedPrice(value);
                                                if (!expectedPriceTouched) {
                                                    setExpectedPriceTouched(true);
                                                }
                                            }
                                        }}
                                        min="0"
                                    />
                                    <span className="suffix"></span>
                                </div>
                                {!isExpectedPriceValid && expectedPriceTouched ? (
                                    <p className="no-price-message" style={{ marginTop: '8px' }}>
                                        Bitte geben Sie einen Wunschpreis an.
                                    </p>
                                ) : null}
                            </div>
                            
                            <div className="contact-request-actions">
                                <Link 
                                    to="/whatsapp-contact" 
                                    className="whatsapp-request-button"
                                    onClick={(e) => {
                                        if (!isExpectedPriceValid) {
                                            e.preventDefault();
                                            setExpectedPriceTouched(true);
                                        }
                                    }}
                                    state={{
                                        carInfo: {
                                            make: selectedValues.marke || '',
                                            model: selectedValues.modell || '',
                                            year: selectedValues.baujahr || '',
                                            bodyType: selectedValues.bauform || '',
                                            fuelType: selectedValues.kraftstoff || '',
                                            power: selectedValues.kw_ps || '',
                                            variant: selectedValues.modellvariante || '',
                                            transmission: selectedValues.getriebeart || '',
                                            doors: selectedValues.tueren || '',
                                            type: selectedValues.modelltyp || '',
                                            mileage: kilometerstand || '',
                                            expectedPrice: expectedPrice || ''
                                        }
                                    }}
                                >
                                    <FaWhatsapp className="whatsapp-icon" />
                                    <span>Preis über WhatsApp anfragen</span>
                                </Link>
                                <Link 
                                    to="/email-contact" 
                                    className="email-request-button"
                                    onClick={(e) => {
                                        if (!isExpectedPriceValid) {
                                            e.preventDefault();
                                            setExpectedPriceTouched(true);
                                        }
                                    }}
                                    state={{
                                        carInfo: {
                                            make: selectedValues.marke || '',
                                            model: selectedValues.modell || '',
                                            year: selectedValues.baujahr || '',
                                            bodyType: selectedValues.bauform || '',
                                            fuelType: selectedValues.kraftstoff || '',
                                            power: selectedValues.kw_ps || '',
                                            variant: selectedValues.modellvariante || '',
                                            transmission: selectedValues.getriebeart || '',
                                            doors: selectedValues.tueren || '',
                                            type: selectedValues.modelltyp || '',
                                            mileage: kilometerstand || '',
                                            expectedPrice: expectedPrice || ''
                                        }
                                    }}
                                >
                                    <FaEnvelope className="whatsapp-icon" />
                                    <span>Preis per E-Mail anfragen</span>
                                </Link>
                            </div>
                            
                            <p className="guarantee-text">
                                <FaCheckCircle className="check-icon" /> Unverbindliche Anfrage, Antwort innerhalb von 24h
                            </p>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };
    
    const phoneNumber = '+4915730710451'; // Replace with your actual number
    const message = `Hallo, ich interessiere mich für ein Angebot für mein Auto: ${selectedValues.marke || ''} ${selectedValues.modell || ''}`; 
    const whatsappLink = `https://wa.me/${phoneNumber}&text=${encodeURIComponent(message)}`;

    return (
        <div className="main-container">
            {/* Hero Section with Car Background */}
            <section className="hero">
                <div className="hero-overlay"></div>
                <div className="hero-content">
                    <h1>Verkaufen Sie Ihr Auto zum besten Preis</h1>
                    <p className="hero-subtitle">Schnell, einfach und transparent – ohne versteckte Kosten</p>
                    
                    <div className="hero-features">
                        <div className="feature-item">
                            <FaCar className="feature-icon" />
                            <span>Kostenlose Bewertung</span>
                        </div>
                        <div className="feature-item">
                            <FaMoneyBillWave className="feature-icon" />
                            <span>Sofortige Auszahlung</span>
                        </div>
                        <div className="feature-item">
                            <FaCheckCircle className="feature-icon" />
                            <span>Garantierter Ankauf</span>
                        </div>
                    </div>

                    <button 
                        className="hero-cta" 
                        onClick={() => {
                            const formSection = document.querySelector('.form-card');
                            formSection.scrollIntoView({ behavior: 'smooth' });
                        }}
                    >
                        Jetzt Auto bewerten
                    </button>
                </div>
            </section>

            {/* Form Section */}
            <div className="content-wrapper">
                <div className="form-card" ref={formCardRef}>
                    <div className="form-header">
                        <h2>Erhalten Sie ein Angebot in 3 einfachen Schritten</h2>
                        <p>Geben Sie Ihre Fahrzeugdaten ein und erhalten Sie ein sofortiges Angebot</p>
                    </div>
                    
                    {renderProgressBar()}
                    
                    <div className="form-content">
                        {renderDropdowns()}
                    </div>
                    
                    <div className="form-navigation">
                        {currentStep > 1 ? (
                            <button 
                                className="nav-button back-button" 
                                onClick={() => {
                                    if (currentStep === 3) {
                                        setPrice(null); // Reset price when going back from the price step
                                        setCalculating(false);
                                    }
                                    setCurrentStep(currentStep - 1);
                                }}
                            >
                                Zurück
                            </button>
                        ) : <div></div>}
                        
                        {currentStep < 3 ? (
                            <button 
                                className={`nav-button next-button ${areCurrentStepFieldsFilled() ? '' : 'disabled'}`}
                                onClick={() => {
                                    if (areCurrentStepFieldsFilled()) {
                                        setCurrentStep(currentStep + 1);
                                    }
                                }}
                                disabled={!areCurrentStepFieldsFilled()}
                            >
                                Weiter <FaArrowRight />
                            </button>
                        ) : null}
                        
                        {currentStep === 3 && price ? (
                            <div className="whatsapp-button-container contact-action-container">
                                <div className="contact-action-stack">
                                    <a 
                                        href={whatsappLink} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="whatsapp-button"
                                    >
                                        <FaWhatsapp className="whatsapp-icon" />
                                        <div className="whatsapp-text-container">
                                            <span className="whatsapp-text">Termin vereinbaren via WhatsApp</span>
                                            <span className="button-subtext">Unverbindliches Beratungsgespräch</span>
                                        </div>
                                    </a>

                                    <Link
                                        to="/email-contact"
                                        className="email-button"
                                        state={{
                                            carInfo: {
                                                make: selectedValues.marke || '',
                                                model: selectedValues.modell || '',
                                                year: selectedValues.baujahr || '',
                                                bodyType: selectedValues.bauform || '',
                                                fuelType: selectedValues.kraftstoff || '',
                                                power: selectedValues.kw_ps || '',
                                                variant: selectedValues.modellvariante || '',
                                                transmission: selectedValues.getriebeart || '',
                                                doors: selectedValues.tueren || '',
                                                type: selectedValues.modelltyp || '',
                                                mileage: kilometerstand || '',
                                                offerPrice: price || ''
                                            }
                                        }}
                                    >
                                        <FaEnvelope className="whatsapp-icon" />
                                        <div className="whatsapp-text-container">
                                            <span className="whatsapp-text">Termin vereinbaren per E-Mail</span>
                                            <span className="button-subtext">Sie erhalten eine Bestätigung per E-Mail</span>
                                        </div>
                                    </Link>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* Process Section with Icons */}
                <section className="process-section">
                    <h2>Wie funktioniert der Verkauf?</h2>
                    <div className="process-steps">
                        <div className="process-step">
                            <div className="process-icon">1</div>
                            <h3>Fahrzeugdaten angeben</h3>
                            <p>Füllen Sie das Formular mit den Details Ihres Fahrzeugs aus.</p>
                        </div>
                        <div className="process-step">
                            <div className="process-icon">2</div>
                            <h3>Sofortiges Angebot</h3>
                            <p>Erhalten Sie ein faires und transparentes Angebot für Ihr Auto.</p>
                        </div>
                        <div className="process-step">
                            <div className="process-icon">3</div>
                            <h3>Termin vereinbaren</h3>
                            <p>Vereinbaren Sie einen Termin zur Fahrzeuguntersuchung.</p>
                        </div>
                        <div className="process-step">
                            <div className="process-icon">4</div>
                            <h3>Sofortige Auszahlung</h3>
                            <p>Nach Abschluss erhalten Sie sofort Ihr Geld.</p>
                        </div>
                    </div>
                </section>
                
                {/* FAQ Section */}
                <section className="faq-section" id="faq">
                    <h2>Häufig gestellte Fragen</h2>
                    <div className="faq-container">
                        {faqData.map((faq, index) => (
                            <div className="faq-item" key={index}>
                                <div 
                                    className="faq-question"
                                    onClick={() => setFaqOpen(prev => ({...prev, [index]: !prev[index]}))}
                                >
                                    <h3>{faq.question}</h3>
                                    {faqOpen[index] ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                                <div className={`faq-answer ${faqOpen[index] ? 'open' : ''}`}>
                                    <p>{faq.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
                
                {/* Enhanced Testimonials Section */}
                {/* <section className="testimonials">
                    <h2>Was unsere Kunden sagen</h2>
                    <div className="testimonial-grid">
                        <div className="testimonial-card">
                            <div className="testimonial-profile">
                                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Kundenfoto" />
                            </div>
                            <div className="testimonial-rating">★★★★★</div>
                            <p className="testimonial-text">
                                "Der gesamte Prozess war extrem einfach. Ich habe online ein Angebot bekommen und konnte mein Auto am nächsten Tag verkaufen."
                            </p>
                            <div className="testimonial-author">Michael K. aus München</div>
                            <div className="testimonial-car">Verkauft: Audi A4 (2018)</div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-profile">
                                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Kundenfoto" />
                            </div>
                            <div className="testimonial-rating">★★★★★</div>
                            <p className="testimonial-text">
                                "Sehr professioneller Service und ein fairer Preis für mein Auto. Absolut empfehlenswert!"
                            </p>
                            <div className="testimonial-author">Sandra M. aus Berlin</div>
                            <div className="testimonial-car">Verkauft: VW Golf (2016)</div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-profile">
                                <img src="https://randomuser.me/api/portraits/men/62.jpg" alt="Kundenfoto" />
                            </div>
                            <div className="testimonial-rating">★★★★★</div>
                            <p className="testimonial-text">
                                "Schnelle Abwicklung und transparente Preisgestaltung. So macht Autoverkauf Spaß!"
                            </p>
                            <div className="testimonial-author">Thomas H. aus Hamburg</div>
                            <div className="testimonial-car">Verkauft: BMW 3er (2019)</div>
                        </div>
                    </div>
                </section> */}
                
                {/* Trust Badges Section */}
                <section className="trust-section">
                    <h2>Ihre Vorteile bei autozack.de</h2>
                    <div className="trust-badges">
                        <div className="trust-badge">
                            <img src="https://cdn-icons-png.flaticon.com/512/1161/1161388.png" alt="Garantie" />
                            <h3>Bestpreis-Garantie</h3>
                            <p>Wir garantieren Ihnen den bestmöglichen Preis für Ihr Fahrzeug.</p>
                        </div>
                        <div className="trust-badge">
                            <img src="https://cdn-icons-png.flaticon.com/512/2910/2910934.png" alt="Sicherheit" />
                            <h3>Sichere Abwicklung</h3>
                            <p>Rechtskonformer Kaufvertrag und sichere Zahlungsabwicklung.</p>
                        </div>
                        <div className="trust-badge">
                            <img src="https://cdn-icons-png.flaticon.com/512/1380/1380338.png" alt="Schnelligkeit" />
                            <h3>Sofortiger Ankauf</h3>
                            <p>Keine Wartezeiten - Erhalten Sie ein Angebot noch am selben Tag.</p>
                        </div>
                    </div>
                </section>
            </div>
            
            {/* Footer with enhanced design */}
            <footer className="main-footer">
                <div className="footer-content">
                    <div className="footer-section">
                        <h3>Über uns</h3>
                        <p>autozack.de ist Ihr zuverlässiger Partner für den einfachen und transparenten Autoverkauf in Deutschland.</p>
                    </div>
                    <div className="footer-section">
                        <h3>Kontakt</h3>
                        {/* <p>Email: info@autozack.de</p> */}
                        <p>Telefon: +49 171 6121034</p>
                        <p>Adresse: Hänfligstraße 10, 45472 Mülheim an der Ruhr</p>
                    </div>
                    {/* <div className="footer-section">
                        <h3>Folgen Sie uns</h3>
                        <div className="social-icons">
                            <a href="#" className="social-icon">f</a>
                            <a href="#" className="social-icon">in</a>
                            <a href="#" className="social-icon">ig</a>
                        </div>
                    </div> */}
                </div>
                <div className="footer-bottom">
                    <p>&copy; 2024 autozack.de - Alle Rechte vorbehalten</p>
                    <div className="footer-links">
                        <Link to="/impressum">Impressum</Link>
                        <Link to="/impressum">Datenschutz</Link>
                        <Link to="/impressum">AGB</Link>
                    </div>
                </div>
            </footer>

            {/* Live Chat Widget */}
            {/* <div className={`chat-widget ${showChat ? 'open' : ''}`}>
                <div className="chat-header">
                    <div className="chat-title">
                        <div className="chat-avatar">
                            <img src="https://randomuser.me/api/portraits/women/22.jpg" alt="Support Agent" />
                        </div>
                        <div>
                            <h3>Live Support</h3>
                            <span className="chat-status">Online</span>
                        </div>
                    </div>
                    <button className="chat-close" onClick={() => setShowChat(false)}>
                        <FaTimes />
                    </button>
                </div>
                <div className="chat-messages">
                    {chatMessages.map((msg, index) => (
                        <div key={index} className={`chat-message ${msg.sender}`}>
                            {msg.text}
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>
                <form className="chat-input" onSubmit={handleChatSubmit}>
                    <input
                        type="text"
                        placeholder="Schreiben Sie eine Nachricht..."
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                    />
                    <button type="submit">Senden</button>
                </form>
            </div> */}

            {/* {!showChat && (
                <button 
                    className="chat-button" 
                    onClick={() => setShowChat(true)}
                    aria-label="Chat öffnen"
                >
                    <FaComments />
                    <span className="chat-button-text">Chat</span>
                </button>
            )} */}
        </div>
    );
}
